import { useCallback, useEffect, useReducer, useRef } from 'react';

interface IUseAccessibleDropdownProps<T> {
  isMainInputFocused: boolean;
  isMainInputTypingEnabled: boolean;
  options: T[];
  optionsRef: React.RefObject<HTMLElement>;
  onChange: (item: T) => void;
  setMainInputFocus: () => void;
}

interface IReducerInitialState {
  dropdownOpened: boolean;
  optionsActive: boolean;
  activeIndex: number;
}

enum ActionType {
  OpenDropDown = 'openDropdown',
  CloseDropdown = 'closeDropdown',
  MoveToNextOption = 'moveToNextOption',
  MoveToPreviousOption = 'moveToPreviousOption',
  ChooseOption = 'chooseOption',
  Clear = 'clear',
}

type ReducerActions =
  | {
      type: ActionType.OpenDropDown;
    }
  | {
      type: ActionType.CloseDropdown;
    }
  | {
      type: ActionType.MoveToNextOption;
      payload: {
        activeIndex: number;
      };
    }
  | {
      type: ActionType.MoveToPreviousOption;
      payload: {
        activeIndex: number;
      };
    }
  | {
      type: ActionType.ChooseOption;
    }
  | {
      type: ActionType.Clear;
    };

const initialState: IReducerInitialState = {
  dropdownOpened: false,
  optionsActive: false,
  activeIndex: -1,
};

const getNextIndex = (current: number, length: number) =>
  current + 1 === length ? 0 : current + 1;
const getPreviousIndex = (current: number, length: number) =>
  current - 1 < 0 ? length - 1 : current - 1;

function reducer(state: IReducerInitialState, action: ReducerActions) {
  switch (action.type) {
    case ActionType.OpenDropDown:
      return { ...state, dropdownOpened: true };
    case ActionType.CloseDropdown:
      return initialState;
    case ActionType.MoveToNextOption:
      return {
        ...state,
        optionsActive: true,
        activeIndex: action.payload.activeIndex,
      };
    case ActionType.MoveToPreviousOption:
      return {
        ...state,
        optionsActive: true,
        activeIndex: action.payload.activeIndex,
      };
    case ActionType.Clear:
    case ActionType.ChooseOption:
      return initialState;

    default:
      return { ...state };
  }
}

const dropdownIsOpenedCallbacks = ({
  chooseOption,
  optionsLength,
  dispatch,
  activeIndex,
  isMainInputTypingEnabled,
  setMainInputFocus,
}: {
  chooseOption: (index: number) => void;
  optionsLength: number;
  dispatch: React.Dispatch<ReducerActions>;
  activeIndex: number;
  isMainInputTypingEnabled: boolean;
  setMainInputFocus: () => void;
}) => {
  const keyDownCallback = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
      case ' ':
        if (activeIndex !== -1) {
          e.preventDefault();
          chooseOption(activeIndex);
          dispatch({ type: ActionType.ChooseOption });
        } else if (!isMainInputTypingEnabled) {
          e.preventDefault();
          dispatch({ type: ActionType.CloseDropdown });
        }

        return;

      case 'Escape':
        e.preventDefault();
        dispatch({ type: ActionType.CloseDropdown });
        setMainInputFocus();

        return;

      case 'ArrowDown':
        e.preventDefault();

        if (optionsLength) {
          dispatch({
            type: ActionType.MoveToNextOption,
            payload: {
              activeIndex: getNextIndex(activeIndex, optionsLength),
            },
          });
        }

        return;

      case 'ArrowUp':
        e.preventDefault();

        if (optionsLength) {
          dispatch({
            type: ActionType.MoveToPreviousOption,
            payload: {
              activeIndex: getPreviousIndex(activeIndex, optionsLength),
            },
          });
        }

        return;

      case 'Tab':
        if (activeIndex !== -1) {
          e.preventDefault();
        }
    }
  };

  document.addEventListener('keydown', keyDownCallback);

  return () => {
    document.removeEventListener('keydown', keyDownCallback);
  };
};

const dropdownIsClosedCallbacks = ({
  dispatch,
  isMainInputTypingEnabled,
}: {
  dispatch: React.Dispatch<ReducerActions>;
  isMainInputTypingEnabled: boolean;
}) => {
  const keyDownCallback = (e: KeyboardEvent) => {
    switch (e.key) {
      case ' ':
        if (!isMainInputTypingEnabled) {
          e.preventDefault();
          dispatch({ type: ActionType.OpenDropDown });
        }
    }
  };

  document.addEventListener('keydown', keyDownCallback);

  return () => {
    document.removeEventListener('keydown', keyDownCallback);
  };
};

const useAccessibleDropdown = <T>({
  options,
  optionsRef,
  onChange,
  isMainInputFocused,
  isMainInputTypingEnabled,
  setMainInputFocus,
}: IUseAccessibleDropdownProps<T>) => {
  const [{ activeIndex, dropdownOpened, optionsActive }, dispatch] = useReducer(
    reducer,
    initialState,
  );

  const chooseOption = useCallback(
    (index: number) => {
      if (options?.[index]) {
        onChange(options[index]);
        setMainInputFocus();
      }
    },
    [onChange, options, setMainInputFocus],
  );

  const handleDropdownClosed = useCallback(() => {
    if (isMainInputFocused && !optionsActive) {
      return dropdownIsClosedCallbacks({ isMainInputTypingEnabled, dispatch });
    }
  }, [isMainInputFocused, isMainInputTypingEnabled, optionsActive]);

  const handleDropdownOpened = useCallback(() => {
    if (isMainInputFocused || optionsActive) {
      return dropdownIsOpenedCallbacks({
        isMainInputTypingEnabled,
        chooseOption,
        optionsLength: options.length,
        dispatch,
        activeIndex,
        setMainInputFocus,
      });
    } else {
      dispatch({ type: ActionType.CloseDropdown });
    }
  }, [
    activeIndex,
    chooseOption,
    isMainInputFocused,
    isMainInputTypingEnabled,
    options.length,
    optionsActive,
    setMainInputFocus,
  ]);

  const handleDropdownOpenedRef = useRef(handleDropdownOpened);
  const handleDropdownClosedRef = useRef(handleDropdownClosed);

  handleDropdownOpenedRef.current = handleDropdownOpened;

  handleDropdownClosedRef.current = handleDropdownClosed;

  useEffect(() => {
    if (dropdownOpened) {
      return handleDropdownOpenedRef.current();
    } else {
      return handleDropdownClosedRef.current();
    }
  }, [activeIndex, dropdownOpened, isMainInputFocused, options]);

  useEffect(() => {
    if (optionsRef.current?.children?.[activeIndex]) {
      (optionsRef.current?.children[activeIndex] as HTMLElement).focus();
    }
  }, [activeIndex, optionsRef]);

  return {
    dropdownOpened: dropdownOpened,
    toggleDropdown: (value?: boolean) => {
      const shouldOpen = typeof value === 'boolean' ? value : !dropdownOpened;

      dispatch({
        type: shouldOpen ? ActionType.OpenDropDown : ActionType.CloseDropdown,
      });
    },
    optionsActive: optionsActive,
    clear: () => {
      dispatch({ type: ActionType.Clear });
    },
  };
};

export default useAccessibleDropdown;
